import React, { useRef, useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import saveIcon from '../icons/save.svg';
import AddIcon from '../icons/plusB.svg';
import ChevronDownIcon from '../icons/chevron-down.svg';
import ManualIcon from '../icons/editB.svg';
import UploadIcon from '../icons/uploadBB.svg';
import PrintIcon from "../icons/printer.svg";

export const PrimaryButton = ({ label, icon, onClick, ...rest }) => {
  return (
    <button
      onClick={onClick}
      {...rest}
      className="flex items-center justify-center px-2 h-[40px] bg-white shadow-[0px_6px_26px_rgba(98,107,125,0.07)] rounded-xl text-primary font-bold space-x-2"
    >
      {icon && <img src={icon} alt="" className="w-5 h-5" />}
      <span>{label}</span>
    </button>
  );
};

export const SaveCancel = ({
  onCancelClick,
  onSaveClick,
  loading = false,
  disabled = false,
  ...rest
}) => {

  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-4" {...rest}>
      <button
        onClick={onCancelClick}
        disabled={loading}
        className="w-[232px] h-[48px] text-[#333333] bg-transparent font-medium text-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ fontWeight: 600 }}
      >
        {t(`actions.cancel`)}
      </button>

      <button
        onClick={onSaveClick}
        disabled={loading || disabled}
        className="w-[232px] h-[48px] bg-primary shadow-[0px_4px_10px_rgba(190,190,190,0.5)] rounded-[12px] text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ fontWeight: 600 }}
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>{t(`actions.saving`) || 'Enregistrement...'}</span>
          </div>
        ) : (
          t(`actions.save`)
        )}
      </button>
    </div>
  );
};

export const SaveSubmit = ({
  onSubmitClick,
  onSaveClick,
  loading = false,
  ...rest
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-4" {...rest}>
      <button
        onClick={onSaveClick}
        disabled={loading}
        className="w-[232px] h-[48px] text-[#333333] bg-transparent font-medium text-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ fontWeight: 600 }}
      >
        {t(`actions.save`)}
      </button>

      <button
        onClick={onSubmitClick}
        disabled={loading}
        className="w-[232px] h-[48px] bg-primary shadow-[0px_4px_10px_rgba(190,190,190,0.5)] rounded-[12px] text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ fontWeight: 600 }}
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>{t(`actions.submitting`) || 'Soumission...'}</span>
          </div>
        ) : (
          t(`actions.submit`)
        )}
      </button>
    </div>
  );
};

export const DoubleButton = ({ option1, option2, option3, initial, icon, icon1, icon2, icon3, onManualClick, onUploadClick, onUploadClick2, onBothClick }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const fileInputRef = useRef(null);
  const fileInputRef2 = useRef(null);
  const { t } = useTranslation();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFileSelect = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;

    try {
      // Check if onUploadClick accepts a file parameter or can be called without one
      if (onUploadClick) {
        if (onUploadClick.length > 0) {
          // Function expects a parameter
          await onUploadClick(file);
        } else {
          // Function doesn't expect a parameter (like download functions)
          await onUploadClick();
        }
      }
    } finally {
      input.value = "";
    }
  };

  const handleFileSelect2 = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;

    try {
      // Check if onUploadClick2 accepts a file parameter or can be called without one
      if (onUploadClick2) {
        if (onUploadClick2.length > 0) {
          // Function expects a parameter
          await onUploadClick2(file);
        } else {
          // Function doesn't expect a parameter (like download functions)
          await onUploadClick2();
        }
      }
    } finally {
      input.value = "";
    }
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={handleClick}
        aria-controls={Boolean(anchorEl) ? 'simple-menu' : undefined}
        aria-haspopup="true"
        className="flex items-center justify-center px-2 h-[40px] bg-white shadow-[0px_20px_80px_#00000011] rounded-[12px] text-primary font-bold space-x-2"
      >
        <div className="flex items-center space-x-2">
          <img src={icon} alt="Add" className="w-5 h-5" />
          <span style={{ fontFamily: "Montserrat" }}>{initial}</span>
        </div>
        <div className="flex items-center">
          <div
            style={{
              borderLeft: '1px solid #043F88',
              height: '24px',
              opacity: 0.16,
              margin: '0 8px',
            }}
          ></div>
          <img src={ChevronDownIcon} alt="Chevron Down" className="w-4 h-4" />
        </div>
      </button>

      <input
        type="file"
        ref={fileInputRef}
        accept=".xls,.xlsx"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      {onUploadClick2 && (
        <input
          type="file"
          ref={fileInputRef2}
          accept=".xls,.xlsx"
          style={{ display: 'none' }}
          onChange={handleFileSelect2}
        />
      )}

      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          mt: 0.5,
          '& .MuiPaper-root': {
            borderRadius: '8px',
            padding: '0px 0',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          },
          '& .MuiMenuItem-root': {
            fontSize: '14px',
            fontWeight: '500',
            color: '#043F88',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            '&:hover': {
              backgroundColor: '#F5F5F5',
            },
          },
        }}
      >
        {/* Menu Items */}
        <MenuItem onClick={() => { onManualClick?.(); handleClose(); }}>
          {icon1 ? <img src={icon1} alt="Manual Icon" className="w-5 h-5" /> : <img src={ManualIcon} alt="Manual Icon" className="w-5 h-5" />}
          <span className="text-[14px] font-bold text-primary" style={{ fontFamily: "Montserrat" }}>
            {option1}
          </span>
        </MenuItem>
        <div
          style={{
            border: '1px solid #F7F7F7',
            opacity: 1,
            margin: '4px 16px',
          }}
        />
        <MenuItem
          onClick={() => {
            if (onUploadClick) {
              // Check if function expects a file parameter
              if (onUploadClick.length > 0) {
                // Function expects a parameter - trigger file input
                fileInputRef.current.click();
              } else {
                // Function doesn't expect a parameter - call directly (for download functions)
                onUploadClick();
              }
            } else {
              // Default behavior - trigger file input
              fileInputRef.current.click();
            }
            handleClose();
          }}
        >
          {icon2 ? <img src={icon2} alt="Upload Icon" className="w-5 h-5" /> : <img src={UploadIcon} alt="Upload Icon" className="w-5 h-5" />}
          <span className="text-[14px] font-bold text-primary" style={{ fontFamily: "Montserrat" }}>
            {option2}
          </span>
        </MenuItem>

        {option3 && (
          <>
            <div
              style={{
                border: '1px solid #F7F7F7',
                opacity: 1,
                margin: '4px 16px',
              }}
            />

            {onUploadClick2 ? (
              <MenuItem
                onClick={() => {
                  // Check if function expects a file parameter
                  if (onUploadClick2.length > 0) {
                    // Function expects a parameter - trigger file input
                    fileInputRef2.current.click();
                  } else {
                    // Function doesn't expect a parameter - call directly (for download functions)
                    onUploadClick2();
                  }
                  handleClose();
                }}
              >
                {icon3 ? <img src={icon3} alt="Upload Icon 2" className="w-5 h-5" /> : <img src={UploadIcon} alt="Upload Icon 2" className="w-5 h-5" />}
                <span className="text-[14px] font-bold text-primary" style={{ fontFamily: "Montserrat" }}>
                  {option3}
                </span>
              </MenuItem>
            ) : (
              <MenuItem onClick={() => { onBothClick?.(); handleClose(); }}>
                {icon3 ? <img src={icon3} alt="Both Icon" className="w-5 h-5" /> : <img src={icon1} alt="Both Icon" className="w-5 h-5" />}
                <span className="text-[14px] font-bold text-primary" style={{ fontFamily: "Montserrat" }}>
                  {option3}
                </span>
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </div>
  );
};

export const SaveCancelTop = ({ onCancel, onSave, loading = false }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-4">
      <button
        onClick={onCancel}
        disabled={loading}
        className="bg-white text-primary font-bold text-[14px] px-6 py-3 rounded-[12px] hover:bg-[#E5EFFF] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {t(`actions.cancel`)}
      </button>

      <button
        onClick={onSave}
        disabled={loading}
        className="bg-primary text-white font-bold text-[14px] px-4 py-3 rounded-[12px] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>{t(`actions.saving`) || 'Enregistrement...'}</span>
          </>
        ) : (
          <>
            <img src={saveIcon} alt="Save Icon" className="w-5 h-5" />
            {t(`actions.save`)}
          </>
        )}
      </button>
    </div>
  );
};

const Components = {
  PrimaryButton,
  SaveCancel,
  DoubleButton,
  SaveCancelTop,
  SaveSubmit
};

export default Components;
